/**
 * Chamadas HTTP de Outros Bancos, das importações, da calculadora, da
 * recorrência e dos documentos.
 *
 * Papel: aqui ficam as chamadas das rotas de `financas-folha`,
 * `financas-outros-bancos` e `financas-documentos` — a configuração da casa, o
 * mapa CPF→nome, as importações de PDF, a folha XLSX, o último grupo, a
 * calculadora, a recorrência, a aba Outros Bancos e os cartões dela, os
 * lançamentos de outros bancos, o recibo e a ordem.
 *
 * Como usar: as rotas de um cartão levam o favorecido e a competência no
 * caminho (`/financas/outros-bancos/{favorecidoId}/{competencia}/…`), e o
 * favorecido vai codificado. As exclusões passam por `clienteApi.remover`, que
 * não lê resposta: a que devolve a situação do cartão (remover a guia) é lida de
 * novo pela invalidação do cache, como as outras gravações.
 */
import {
  type CalculadoraPetrella,
  type ConfiguracaoFinanceira,
  type ConsultaDoMes,
  type ConsultaDoRecibo,
  type DiasDeOutrosBancos,
  type Favorecido,
  type FolhaFechada,
  type FolhaImportada,
  type GrupoAjudaCusto,
  type GuiaDaeDeOutrosBancos,
  type ImportacaoDaFolha,
  type ImportacaoDaGuiaDae,
  type LancamentoDeOutrosBancos,
  type MesDoFavorecido,
  type NomeDaFolha,
  type NovaFolhaImportada,
  type NovoLancamentoDeOutrosBancos,
  type OrdemDePagamento,
  type OutrosBancosDoMes,
  type ReciboDePagamento,
  type Recorrencia,
  type ResultadoDaImportacao,
  type ResultadoDaRecorrencia,
  type SalarioDeOutrosBancos,
  type SituacaoDoMes,
  configuracaoFinanceiraSchema,
  favorecidoSchema,
  folhaFechadaSchema,
  folhaImportadaSchema,
  grupoAjudaCustoSchema,
  lancamentoDeOutrosBancosSchema,
  nomesDaFolhaSchema,
  ordemDePagamentoSchema,
  outrosBancosDoMesSchema,
  reciboDePagamentoSchema,
  resultadoDaImportacaoSchema,
  resultadoDaRecorrenciaSchema,
  situacaoDoMesSchema,
} from '@concierge/shared';

import { clienteApi } from '@/shared/api';

/**
 * O caminho de uma rota de cartão de Outros Bancos.
 *
 * @param mes - O favorecido e a competência do cartão.
 * @param acao - O fim do caminho: `salario`, `dias`, `guia-dae` ou `fechar`.
 * @returns `/financas/outros-bancos/{favorecidoId}/{competencia}/{acao}`.
 */
function caminhoDoCartao(mes: MesDoFavorecido, acao: string): string {
  return `/financas/outros-bancos/${encodeURIComponent(mes.favorecidoId)}/${mes.competencia}/${acao}`;
}

/**
 * Lê o pagador padrão, as duas siglas e a última verificação da recorrência.
 *
 * @param residenciaId - A casa do caixa.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns A configuração; textos vazios quando a casa não tem linha.
 */
export function buscarConfiguracao(
  residenciaId: string,
  sinal?: AbortSignal,
): Promise<ConfiguracaoFinanceira> {
  return clienteApi.get('/financas/configuracao', {
    schema: configuracaoFinanceiraSchema,
    params: { residenciaId },
    sinal,
  });
}

/**
 * Lê o mapa CPF→nome dos parsers da folha e da calculadora.
 *
 * A rota exige `editar`: quem só vê não importa nada.
 *
 * @param residenciaId - A casa do caixa.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns Os pares, com o CPF só com dígitos.
 */
export function buscarNomesDaFolha(
  residenciaId: string,
  sinal?: AbortSignal,
): Promise<NomeDaFolha[]> {
  return clienteApi.get('/financas/folha/nomes-por-cpf', {
    schema: nomesDaFolhaSchema,
    params: { residenciaId },
    sinal,
  });
}

/**
 * Grava as linhas confirmadas na conferência da folha em PDF.
 *
 * @param corpo - A casa, a competência, a data e as linhas marcadas.
 * @returns Quantos lançamentos nasceram.
 */
export function importarFolha(corpo: ImportacaoDaFolha): Promise<ResultadoDaImportacao> {
  return clienteApi.post('/financas/importacoes/folha', corpo, {
    schema: resultadoDaImportacaoSchema,
  });
}

/**
 * Grava os itens confirmados na conferência da guia DAE.
 *
 * @param corpo - A casa, o período, a data e os itens marcados.
 * @returns Quantos lançamentos nasceram.
 */
export function importarGuiaDae(corpo: ImportacaoDaGuiaDae): Promise<ResultadoDaImportacao> {
  return clienteApi.post('/financas/importacoes/guia-dae', corpo, {
    schema: resultadoDaImportacaoSchema,
  });
}

/**
 * Lê a folha XLSX de um mês.
 *
 * @param consulta - A casa e o mês aberto.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns A folha com o total, ou `null` quando o mês não tem folha.
 */
export function buscarFolhaImportada(
  consulta: ConsultaDoMes,
  sinal?: AbortSignal,
): Promise<FolhaImportada | null> {
  return clienteApi.get('/financas/folhas-importadas', {
    schema: folhaImportadaSchema.nullable(),
    params: { residenciaId: consulta.residenciaId, competencia: consulta.competencia },
    sinal,
  });
}

/**
 * Substitui a folha XLSX do mês.
 *
 * @param corpo - A casa, o mês aberto e os itens lidos da planilha.
 * @returns A folha como ficou gravada.
 */
export function gravarFolhaImportada(corpo: NovaFolhaImportada): Promise<FolhaImportada> {
  return clienteApi.put('/financas/folhas-importadas', corpo, {
    schema: folhaImportadaSchema,
  });
}

/**
 * Remove a folha XLSX do mês, pelo "✕ Remover folha".
 *
 * @param consulta - A casa e o mês aberto.
 */
export function removerFolhaImportada(consulta: ConsultaDoMes): Promise<void> {
  return clienteApi.remover('/financas/folhas-importadas', {
    params: { residenciaId: consulta.residenciaId, competencia: consulta.competencia },
  });
}

/**
 * Lê o grupo de ajuda de custo mais recente de um favorecido.
 *
 * @param favorecidoId - O favorecido digitado na calculadora.
 * @param sinal - Sinal de cancelamento.
 * @returns O grupo com os itens, ou `null` quando ele não tem grupo.
 */
export function buscarUltimoGrupo(
  favorecidoId: string,
  sinal?: AbortSignal,
): Promise<GrupoAjudaCusto | null> {
  return clienteApi.get('/financas/ajuda-custo/ultimo', {
    schema: grupoAjudaCustoSchema.nullable(),
    params: { favorecidoId },
    sinal,
  });
}

/**
 * Grava o que uma aba da Calculadora Petrella pede.
 *
 * @param corpo - A aba e os campos dela; a API recalcula os itens.
 * @returns Quantos lançamentos nasceram.
 */
export function criarPelaCalculadora(
  corpo: CalculadoraPetrella,
): Promise<ResultadoDaImportacao> {
  return clienteApi.post('/financas/calculadora-petrella', corpo, {
    schema: resultadoDaImportacaoSchema,
  });
}

/**
 * Pede a recorrência automática de ajuda de custo.
 *
 * @param corpo - A casa e se a trava diária vale (`forcar` falso) ou não.
 * @returns Se rodou, quantos grupos nasceram e quantos órfãos foram corrigidos.
 */
export function verificarRecorrencia(corpo: Recorrencia): Promise<ResultadoDaRecorrencia> {
  return clienteApi.post('/financas/ajuda-custo/recorrencia', corpo, {
    schema: resultadoDaRecorrenciaSchema,
  });
}

/**
 * Lê a aba Outros Bancos de um mês.
 *
 * @param consulta - A casa e o mês aberto.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns As situações, as folhas fechadas e os lançamentos de outros bancos.
 */
export function buscarOutrosBancos(
  consulta: ConsultaDoMes,
  sinal?: AbortSignal,
): Promise<OutrosBancosDoMes> {
  return clienteApi.get('/financas/outros-bancos', {
    schema: outrosBancosDoMesSchema,
    params: { residenciaId: consulta.residenciaId, competencia: consulta.competencia },
    sinal,
  });
}

/**
 * Inclui um favorecido cadastrado na folha de Outros Bancos.
 *
 * @param favorecidoId - O favorecido escolhido no "+ Novo colaborador".
 * @returns O favorecido, já com a conta apartada.
 */
export function incluirColaborador(favorecidoId: string): Promise<Favorecido> {
  return clienteApi.post(
    '/financas/outros-bancos/colaboradores',
    { favorecidoId },
    { schema: favorecidoSchema },
  );
}

/**
 * Grava o salário líquido de um cartão.
 *
 * @param mes - O favorecido e a competência do cartão.
 * @param corpo - O salário, nulo para apagar, e se veio de importação.
 * @returns A situação do mês como ficou.
 */
export function gravarSalario(
  mes: MesDoFavorecido,
  corpo: SalarioDeOutrosBancos,
): Promise<SituacaoDoMes> {
  return clienteApi.put(caminhoDoCartao(mes, 'salario'), corpo, {
    schema: situacaoDoMesSchema,
  });
}

/**
 * Grava os dois dias de um cartão, como estão à vista.
 *
 * @param mes - O favorecido e a competência do cartão.
 * @param corpo - Os dias da ajuda de custo e das diárias.
 * @returns A situação do mês como ficou.
 */
export function gravarDias(
  mes: MesDoFavorecido,
  corpo: DiasDeOutrosBancos,
): Promise<SituacaoDoMes> {
  return clienteApi.put(caminhoDoCartao(mes, 'dias'), corpo, { schema: situacaoDoMesSchema });
}

/**
 * Substitui a guia DAE de um cartão.
 *
 * @param mes - O favorecido e o mês aberto, onde a guia entra.
 * @param corpo - O período da guia e os itens confirmados.
 * @returns A situação do mês como ficou.
 */
export function gravarGuiaDeOutrosBancos(
  mes: MesDoFavorecido,
  corpo: GuiaDaeDeOutrosBancos,
): Promise<SituacaoDoMes> {
  return clienteApi.put(caminhoDoCartao(mes, 'guia-dae'), corpo, {
    schema: situacaoDoMesSchema,
  });
}

/**
 * Remove a guia DAE de um cartão, sem pedir confirmação.
 *
 * @param mes - O favorecido e o mês aberto.
 */
export function removerGuiaDeOutrosBancos(mes: MesDoFavorecido): Promise<void> {
  return clienteApi.remover(caminhoDoCartao(mes, 'guia-dae'));
}

/**
 * Fecha a folha de um cartão: a fotografia vai para o Fechamento.
 *
 * @param mes - O favorecido e o mês aberto.
 * @returns A folha fechada como ficou gravada.
 */
export function fecharFolha(mes: MesDoFavorecido): Promise<FolhaFechada> {
  return clienteApi.post(caminhoDoCartao(mes, 'fechar'), undefined, {
    schema: folhaFechadaSchema,
  });
}

/**
 * Remove as folhas fechadas de todos os favorecidos do mês.
 *
 * @param consulta - A casa e o mês aberto.
 */
export function removerFolhasFechadas(consulta: ConsultaDoMes): Promise<void> {
  return clienteApi.remover('/financas/folhas-fechadas', {
    params: { residenciaId: consulta.residenciaId, competencia: consulta.competencia },
  });
}

/**
 * Grava um lançamento de outros bancos no mês aberto.
 *
 * @param corpo - O formulário "Novo Lançamento", com a competência do mês aberto.
 * @returns O lançamento como ficou gravado.
 */
export function criarLancamentoDeOutrosBancos(
  corpo: NovoLancamentoDeOutrosBancos,
): Promise<LancamentoDeOutrosBancos> {
  return clienteApi.post('/financas/outros-bancos/lancamentos', corpo, {
    schema: lancamentoDeOutrosBancosSchema,
  });
}

/**
 * Exclui um lançamento de outros bancos.
 *
 * @param id - O identificador do lançamento.
 */
export function excluirLancamentoDeOutrosBancos(id: string): Promise<void> {
  return clienteApi.remover(`/financas/outros-bancos/lancamentos/${encodeURIComponent(id)}`);
}

/**
 * Lê o recibo de um lançamento ou de um cartão de Outros Bancos.
 *
 * @param consulta - O lançamento, ou o favorecido e a competência.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns O recibo pronto para desenhar.
 */
export function buscarRecibo(
  consulta: ConsultaDoRecibo,
  sinal?: AbortSignal,
): Promise<ReciboDePagamento> {
  return clienteApi.get('/financas/documentos/recibo', {
    schema: reciboDePagamentoSchema,
    params:
      'lancamentoId' in consulta
        ? { lancamentoId: consulta.lancamentoId }
        : { favorecidoId: consulta.favorecidoId, competencia: consulta.competencia },
    sinal,
  });
}

/**
 * Lê a ordem de pagamento de um cartão de Outros Bancos.
 *
 * @param mes - O favorecido e a competência.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns A ordem pronta para desenhar.
 */
export function buscarOrdemDePagamento(
  mes: MesDoFavorecido,
  sinal?: AbortSignal,
): Promise<OrdemDePagamento> {
  return clienteApi.get('/financas/documentos/ordem-de-pagamento', {
    schema: ordemDePagamentoSchema,
    params: { favorecidoId: mes.favorecidoId, competencia: mes.competencia },
    sinal,
  });
}
